import { CSSProperties } from 'react';
import {
  DEALER_HEIGHT,
  DEALER_PORTRAIT_HEIGHT,
  DEALER_PORTRAIT_WIDTH,
  DEALER_SEAT_OFFSET_PX,
  DEALER_SEAT_TOP_PX,
  DEALER_WIDTH,
  SEAT_AVATAR_POP_LANDSCAPE,
  SEAT_AVATAR_POP_PORTRAIT,
  SEAT_AVATAR_SCALE_LANDSCAPE,
  SEAT_AVATAR_SCALE_PORTRAIT,
  SEAT_CARD_PADDING_TOP_LANDSCAPE,
  SEAT_CARD_PADDING_TOP_PORTRAIT,
  SEAT_CARD_WIDTH,
  SEAT_NAME_ROW_HEIGHT,
  SEAT_TEXT_MIN_FONT_PX,
  SEAT_TIMER_SIZE,
  SMALL_CARD_HEIGHT,
  SMALL_CARD_WIDTH
} from 'constants/gameConstants';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { TableZ } from 'styles/zIndex';

import { cardCornerRadiusForWidth } from 'components/Card/deck';

/**
 * Horizontal padding the seat card spends on its own border/padding, per side.
 * Subtracted from SEAT_CARD_WIDTH to get the width the name/balance text
 * actually has to live in.
 */
export const SEAT_CARD_PADDING_X = 4;
/** Usable text width inside the fixed-width seat card. */
export const SEAT_TEXT_MAX_WIDTH = SEAT_CARD_WIDTH - SEAT_CARD_PADDING_X * 2;

/**
 * Average glyph advance as a fraction of font size for the seat's typeface
 * (Outfit 600/700, mixed latin + digits + "$,."). Deliberately a single
 * conservative ratio rather than a canvas measurement: seats re-render on every
 * stack tick, and a per-frame text measure is not worth the fidelity here.
 */
export const AVG_GLYPH_WIDTH_RATIO = 0.58;

/** Root font size the seat's design sizes are authored against. */
export const ROOT_FONT_SIZE_PX = 16;

/**
 * Resolve a design font size ('0.9rem' | '14px' | 14) to px, so the fit maths
 * below can compare it against a pixel width. Unknown units fall back to the
 * root size rather than throwing — a mis-typed size should shrink the text, not
 * blank the seat.
 */
export const remToPx = (size: string | number): number => {
  if (typeof size === 'number') return size;
  const value = parseFloat(size);
  if (!Number.isFinite(value)) return ROOT_FONT_SIZE_PX;
  if (size.trim().endsWith('rem') || size.trim().endsWith('em')) {
    return value * ROOT_FONT_SIZE_PX;
  }
  return value;
};

/**
 * Pick the largest design font size at which `text` still fits `maxWidthPx`,
 * never going below `minPx`.
 *
 * Because the seat card and its text scale together via `--seat-scale`, the fit
 * is computed at scale 1 and holds at every scale.
 *
 * Width comes from an average-glyph ESTIMATE here. That is accurate enough for
 * the balance (digits and punctuation, which the ratio was tuned against) but
 * NOT for display names — see `useShrinkToFit`, which measures the element.
 *
 * Pure so the threshold is unit-testable without a DOM.
 */
export const fitFontSizePx = (
  text: string | undefined | null,
  maxWidthPx: number,
  designPx: number,
  minPx: number = SEAT_TEXT_MIN_FONT_PX,
  ratio: number = AVG_GLYPH_WIDTH_RATIO
): number => {
  const length = text ? String(text).length : 0;
  if (length === 0) return designPx;
  const fits = maxWidthPx / (length * ratio);
  return Math.max(minPx, Math.min(designPx, fits));
};

/** Shrink-to-fit, then ellipsis — the seat card's width never moves either way. */
export const ellipsisTextStyle: CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

/**
 * Where the dealer marker sits relative to a seat, derived from the seat's own
 * position rather than hand-placed per seat mode.
 *
 * Two invariants, both of which the old per-mode `right: -25px` / `left: -50px`
 * literals broke:
 *
 *  - it hangs off the edge facing the table CENTRE by DEALER_SEAT_OFFSET_PX —
 *    left-of-midline seats overhang right, right-of-midline seats overhang
 *    left. Because the overhang always points inward it can never push anything
 *    toward a viewport edge, and it reads as belonging to that player rather
 *    than to the felt;
 *  - it is vertically aligned to the DISPLAY NAME row, not the balance row.
 *    Bottom-anchoring it inside the card put it straight through the stack
 *    figure (reported 2026-08-19). Aligning its bottom edge with the name row's
 *    means any excess height spills UP into the empty band behind the popped
 *    avatar rather than back down onto the balance.
 *
 * The hole-card band is centred on the seat card's TOP edge (see
 * `getCardOffset`), well above the name row — so the marker clears the cards in
 * both orientations even though the fan reaches further left than this does.
 */
export const getSeatDealerOffset = (
  position: Pick<SeatPosition, 'leftPct'>,
  isPortrait: boolean
): CSSProperties => {
  const size = isPortrait ? DEALER_PORTRAIT_WIDTH : DEALER_WIDTH;
  const height = isPortrait ? DEALER_PORTRAIT_HEIGHT : DEALER_HEIGHT;
  const paddingTop = isPortrait ? SEAT_CARD_PADDING_TOP_PORTRAIT : SEAT_CARD_PADDING_TOP_LANDSCAPE;
  // Seats left of the felt's midline point right; everything else points left.
  const towardCentreIsRight = (position?.leftPct ?? 50) <= 50;
  return {
    position: 'absolute',
    // PORTRAIT rests up in the empty band beside the popped avatar, clear of
    // the name and balance rows below it — see DEALER_SEAT_TOP_PX.
    //
    // LANDSCAPE keeps the original bottom-of-the-name-row alignment. It has no
    // reason to move (the flank bets stay level with the player there, where
    // there is side room, rather than dropping under the card), and its marker
    // is 6px taller in a card with 1px less padding — so the portrait resting
    // place would lap it over the name row's top.
    top: isPortrait ? DEALER_SEAT_TOP_PX : paddingTop + SEAT_NAME_ROW_HEIGHT - height,
    ...(towardCentreIsRight ? { right: DEALER_SEAT_OFFSET_PX } : { left: DEALER_SEAT_OFFSET_PX }),
    width: size,
    height,
    // Under the seat's own cards (TableZ.withinSeatCards) — the showdown
    // regression this ticket fixes.
    zIndex: TableZ.withinSeatDealer,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%'
  };
};

/**
 * Font size with a readable floor under responsive scaling. Seats are scaled by
 * `transform: scale(var(--seat-scale))` (× any fixed per-seat shrink), which
 * shrinks text too. Dividing the floor by that scale makes the font grow as the
 * seat shrinks, so the *rendered* size never drops below `minPx` — while staying
 * at the design size when the table is full-size.
 *
 * @param design   design font size (e.g. '0.82rem')
 * @param minPx    minimum rendered size in px
 * @param fixedScale extra fixed scale the element sits under (e.g. open seat's 0.7/0.85)
 */
export const minScaledFontSize = (
  design: string,
  minPx = SEAT_TEXT_MIN_FONT_PX,
  fixedScale = 1
): string =>
  // NO `--table-view-scale` here any more. It is folded into `--seat-scale`
  // (Table/index.tsx), which this already divides by — multiplying again would
  // apply the player's setting twice, quadratically.
  //
  // This also settles the bug recorded in the balance-row docblock below: the
  // text used to be grown by the view scale inside a seat card whose width did
  // NOT take that scale, so a larger Table View Size overflowed the box and the
  // balance came out as "$1,670…" (reported 2026-09-08). The box now scales with
  // the setting too, so the text and the space it has grow together and the
  // overflow cannot recur.
  `calc(max(${design}, calc(${minPx}px / (${fixedScale} * var(--seat-scale, 1)))))`;

export const getSeatCardStyle = (isPortrait: boolean): CSSProperties => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  borderRadius: isPortrait ? '8px' : '10px',
  // The band the popped avatar sits in. Shared with getSeatDealerOffset, which
  // lines the dealer marker up against the name row directly below it.
  paddingTop: isPortrait ? SEAT_CARD_PADDING_TOP_PORTRAIT : SEAT_CARD_PADDING_TOP_LANDSCAPE,
  paddingBottom: isPortrait ? '3px' : '5px',
  paddingLeft: SEAT_CARD_PADDING_X,
  paddingRight: SEAT_CARD_PADDING_X,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'rgba(255, 255, 255, 0.08)',
  // FIXED width, not a minimum. The old `minWidth` let the card grow with the
  // player's name, which moved every seat's spacing, its cards and its dealer
  // marker around depending on who was sitting there. The name now fits by
  // shrinking (useShrinkToFit) inside this box instead of pushing it wider.
  //
  // SEAT_CARD_WIDTH, not SEAT_CONTAINER_WIDTH: the card is wider than the
  // avatar it sits under, which buys the name several characters at a readable
  // size without touching the avatar / timer-ring geometry.
  width: SEAT_CARD_WIDTH,
  boxSizing: 'border-box',
  overflow: 'visible'
});

/** The avatar's own scale inside its popped wrapper. */
export const seatAvatarScale = (isPortrait: boolean): number =>
  isPortrait ? SEAT_AVATAR_SCALE_PORTRAIT : SEAT_AVATAR_SCALE_LANDSCAPE;

/**
 * How far the avatar PAINTS above the seat card's top edge, in px.
 *
 * Not simply the pop offset: that positions a SEAT_TIMER_SIZE-tall wrapper, and
 * the avatar is scaled about its own centre inside it — so the painted top sits
 * half the shortfall BELOW the wrapper's top. Anything stacking above the avatar
 * (the action bubble) has to clear this, not the raw offset, or it floats with a
 * visible gap.
 */
export const seatAvatarPaintedReach = (isPortrait: boolean): number => {
  const popTop = isPortrait ? SEAT_AVATAR_POP_PORTRAIT : SEAT_AVATAR_POP_LANDSCAPE;
  return popTop - (SEAT_TIMER_SIZE * (1 - seatAvatarScale(isPortrait))) / 2;
};

/**
 * The action bubble's label metrics. Shared with the bet placement
 * (hooks/useEllipticalPositions), which has to keep a chip out of the bubble's
 * airspace and so needs to know how tall it paints.
 */
export const SEAT_ACTION_BUBBLE_FONT_SIZE = '0.55rem';
export const SEAT_ACTION_BUBBLE_LINE_HEIGHT = 1.25;
export const SEAT_ACTION_BUBBLE_PADDING_Y_PX = 3;

/**
 * The action bubble's painted height in UNSCALED px, at a given `--seat-scale`.
 *
 * Not a constant: the label is floored at SEAT_TEXT_MIN_FONT_PX of RENDERED size
 * (minScaledFontSize), so the smaller the seat is painted, the taller the bubble
 * is relative to the seat furniture. At a 390px phone's ~0.64 scale the floor is
 * what sets it, and the bubble is half as tall again as its design size.
 */
export const seatActionBubbleHeight = (seatScale: number): number => {
  const fontPx = Math.max(
    remToPx(SEAT_ACTION_BUBBLE_FONT_SIZE),
    SEAT_TEXT_MIN_FONT_PX / (seatScale || 1)
  );
  return fontPx * SEAT_ACTION_BUBBLE_LINE_HEIGHT + SEAT_ACTION_BUBBLE_PADDING_Y_PX * 2;
};

export const getAvatarPopStyle = (isPortrait: boolean): CSSProperties => ({
  position: 'absolute',
  top: `${-(isPortrait ? SEAT_AVATAR_POP_PORTRAIT : SEAT_AVATAR_POP_LANDSCAPE)}px`,
  left: '50%',
  transform: 'translateX(-50%)',
  // Deliberately UNDER the seat's hole cards (TableZ.withinSeatCards): a
  // revealed hand covering the portrait is fine, a portrait covering the hand
  // is not.
  zIndex: TableZ.withinSeatPlayer,
  overflow: 'visible'
});

export const dealerButtonStyle: CSSProperties = {
  background: 'linear-gradient(135deg, #d4a853 0%, #b8912e 100%)',
  width: DEALER_WIDTH,
  height: DEALER_HEIGHT,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  color: '#1a1408',
  zIndex: 5,
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
  border: '1.5px solid rgba(255, 255, 255, 0.2)',
  position: 'absolute',
  top: '0px',
  left: '80px'
};

export const cardOutlineStyle: CSSProperties = {
  // Exactly the card that lands in it — the felt SmallCard's box, with the deck
  // artwork's own corner radius (EN-10656).
  width: SMALL_CARD_WIDTH,
  height: SMALL_CARD_HEIGHT,
  borderRadius: cardCornerRadiusForWidth(SMALL_CARD_WIDTH),
  border: '2px dashed rgba(255, 255, 255, 0.15)',
  boxSizing: 'border-box'
};

// `actionInlineStyle` lived here — the pill stacked UNDER an opponent's name and
// balance. Replaced by components/SeatActionBubble, which speaks from the
// portrait instead of adding another row of seat data.

export const actionBadgeStyle: CSSProperties = {
  position: 'absolute',
  top: '-7px',
  right: '-4px',
  fontSize: '0.55rem',
  fontWeight: 800,
  color: '#ffffff',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  padding: '2px 6px',
  borderRadius: '6px',
  zIndex: 5,
  whiteSpace: 'nowrap',
  lineHeight: 1.3
};

export const seatCardInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0px',
  whiteSpace: 'nowrap',
  // Bound the text block to the fixed card width so `text-overflow: ellipsis`
  // on the rows below has something to overflow against. `minWidth: 0` defeats
  // a flex item's default min-content floor, which would otherwise let a long
  // name push the card wide again.
  width: '100%',
  minWidth: 0,
  textAlign: 'center'
};

/**
 * The name row WITHOUT a font size.
 *
 * `useShrinkToFit` sets `fontSize` on the element directly, so React must not
 * manage that property — if it did, every re-render (a stack tick, a timer)
 * would reset the fitted size and the name would clip again.
 *
 * `overflow: hidden` + `whiteSpace: nowrap` are what make `scrollWidth` report
 * the single-line width the fit is measured against.
 *
 * `textOverflow` has flipped three times, so the current state is spelled out
 * rather than left to be re-derived: absent at first (an ellipsis hides which
 * player you are looking at), then `ellipsis` because with nothing to stop it
 * the fit took a 12-char name down to 8.91px against a short name's 14.05px
 * (reported 2026-08-27), then absent again — product decision 2026-09-17, the
 * name always renders complete.
 *
 * What makes it absent SAFE this time is that the readable floor no longer
 * competes with fitting: `SEAT_NAME_MIN_PAINTED_PX` is 0, so the fit cannot
 * bottom out and hand a tail to an ellipsis. If a non-zero floor is ever
 * restored here, `textOverflow: 'ellipsis'` has to come back in the same change
 * or the name hard-clips mid-glyph instead.
 */
export const opponentNameBase: CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  // `overflow: hidden` is LOAD-BEARING, not decoration: `scrollWidth` only
  // reports the single-line width useShrinkToFit measures against while the box
  // clips. Remove it and `needed` collapses to `available`, the shrink ratio
  // goes to ~1, and the name spills across the felt instead of fitting.
  overflow: 'hidden',
  // NO `textOverflow` — the name never truncates (see SEAT_NAME_MIN_PAINTED_PX,
  // now 0). Nothing is left for an ellipsis to do, and leaving one would only
  // hide a failure that can no longer happen.
  whiteSpace: 'nowrap',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.85)'
};

/** Name row at its design size, for callers that do not fit it to the card. */
export const opponentName: CSSProperties = {
  ...opponentNameBase,
  fontSize: '0.72rem'
};

/**
 * The balance row WITHOUT a font size — `useShrinkToFit` owns `fontSize` here,
 * exactly as it does for `opponentNameBase`, so React must not set one or every
 * stack tick would reset the fitted size.
 *
 * Why this row moved off the predicted fit (`fitFontSizePx` + `minScaledFontSize`):
 * those two pulled in opposite directions. The estimate sized the text to fit
 * SEAT_TEXT_MAX_WIDTH at scale 1, and `minScaledFontSize` then multiplied the
 * result by `var(--table-view-scale)` and raised it to hold a painted-px floor —
 * growing the text back past the width it had just been fitted to. The seat card
 * is a FIXED SEAT_CARD_WIDTH and does NOT take that scale, so a larger Table
 * View Size grew the balance inside a box that never grew with it, and the
 * overflow came out as "$1,670…" (reported 2026-09-08).
 *
 * Measuring the element removes every one of those inputs — the painted scale,
 * the view-size multiplier, the 700 weight and the webfont are all already in
 * `scrollWidth`. See `useShrinkToFit`.
 *
 * `textOverflow: ellipsis` stays as a backstop for a value no floor could fit,
 * but SEAT_STACK_MIN_FONT_PX is set low enough that real balances shrink instead
 * of reaching it.
 */
export const opponentStackBase: CSSProperties = {
  ...ellipsisTextStyle,
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.6)'
};

/** Balance row at its design size, for callers that do not fit it to the card. */
export const opponentStack: CSSProperties = {
  ...opponentStackBase,
  fontSize: '0.68rem'
};

/**
 * Widest the POV row may lay out, in layout px.
 *
 * The row is an absolutely positioned, shrink-to-fit box, so until now its width
 * came from whatever room the felt happened to leave beside it — different on
 * every viewport and in every grid cell, and never measured. `useShrinkToFit`
 * needs a box it can measure the name against, so the budget is stated here
 * instead of inherited by accident.
 *
 * Half of the narrowest table the grid supports (MultiTableGrid's 390px floor,
 * an iPhone-13 logical width). That is what the browser already resolves to for
 * a centred POV seat in the tightest supported cell, so stating it changes no
 * layout that works today — it only stops the row growing without bound on a
 * wide felt, which is what let a long name push the fit off the end.
 */
export const POV_ROW_MAX_WIDTH = 195;

export const myPlayerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  maxWidth: POV_ROW_MAX_WIDTH,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderRadius: '20px',
  padding: '2px 12px 2px 2px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'rgba(255, 255, 255, 0.08)'
};

export const myPlayerInfoCol: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  whiteSpace: 'nowrap',
  // Defeats a flex item's default min-content floor. Without it the column
  // refuses to go narrower than the name, so the ROW grows to fit the text
  // instead of the text shrinking to fit the row — and the fit has no bounded
  // box left to measure against. Same reason seatCardInfoStyle sets it.
  minWidth: 0
};

/**
 * Portrait current-player (POV) name/stack — sized up to match the larger
 * portrait opponent text (see SeatCardPill). These styles are POV-only.
 *
 * NO `fontSize` on either row: `useShrinkToFit` owns it, exactly as it does for
 * `opponentNameBase` / `opponentStackBase`. React must not manage that property
 * or every re-render (a stack tick, a timer) resets the fitted size.
 *
 * They used to be sized by `minScaledFontSize`, a PREDICTED size with no
 * measurement and no shrink step. It divides a painted-px floor by
 * `--seat-scale`, so the LAYOUT font GREW as the table shrank — max(0.9rem,
 * 10px / 0.53) = 18.87px in a multi-table grid cell, and 22.6px again at the
 * largest Table View Size — inside a row whose layout width did not grow with
 * it. The overflow came out as the reported "Connsack" -> "Connsa…" in mobile
 * portrait and grid view, while LONGER names on the same screen rendered whole
 * because they were opponent seats on the measured path (reported 2026-09-15).
 *
 * `ellipsisTextStyle` stays, and deliberately, but only for the `nowrap` +
 * `overflow: hidden` the hook's contract needs to read `scrollWidth` at all.
 * The NAME overrides its ellipsis below (product decision 2026-09-17), which is
 * safe only because `SEAT_NAME_MIN_PAINTED_PX` is 0 and the fit can no longer
 * bottom out; the BALANCE keeps the ellipsis, because its floor is still real.
 */
export const myPlayerName: CSSProperties = {
  ...ellipsisTextStyle,
  // Overrides the shared base's ellipsis: this is a NAME row, and the name
  // always renders complete. `ellipsisTextStyle` still supplies the nowrap /
  // hidden / maxWidth the fit measures against, and the balance below keeps the
  // ellipsis it brings.
  textOverflow: 'clip',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.85)'
};

export const myPlayerStack: CSSProperties = {
  ...ellipsisTextStyle,
  fontWeight: 700,
  color: '#a8d88e'
};

// Bounty bullets sit IMMEDIATELY OUTSIDE the pill's right edge, anchored
// vertically below where the hole cards end. Cards render at the
// avatar's vertical band (top of the seat) and can land on either side
// of the avatar depending on seat position, so anything in the upper
// band fights them on roughly half the table. The pill's right side,
// below the card band, is reliably empty in every seat layout — that
// dead space is the clean home for the badge. The negative `right`
// pushes it just past the pill border so it reads as a tab attached to
// the seat card rather than floating inside the pill's content area.
export const bountyBadgePositionStyle: CSSProperties = {
  position: 'absolute',
  top: '6px',
  right: '-28px',
  zIndex: 4
};
